import React, { useMemo } from "react";
import {
  AccountBalanceWallet,
  CalendarMonth,
  DateRange,
  LocalDining,
  Payment,
  ReceiptLong,
  ShoppingBag,
  Speed,
  StoreMallDirectory,
  TrendingUp,
  WarningAmber,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectOrders } from "../Store/Slices/restaurantSlice";
import styles from "./DashboardPage.module.css";

const startOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const bucketByDay = (orders, days) => {
  const now = new Date();
  const start = startOfDay(addDays(now, -(days - 1)));
  const totals = [];

  for (let i = 0; i < days; i += 1) {
    const current = addDays(start, i);
    const key = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`;
    totals.push({
      key,
      label:
        days <= 7
          ? current.toLocaleDateString("en-US", { weekday: "short" })
          : current.toLocaleDateString("en-US", { day: "2-digit", month: "short" }),
      orders: 0,
      amount: 0,
    });
  }

  const indexByKey = new Map(totals.map((item, index) => [item.key, index]));

  orders.forEach((order) => {
    const created = new Date(order.createdAt || Date.now());
    if (created < start) return;
    const createdKey = `${created.getFullYear()}-${created.getMonth()}-${created.getDate()}`;
    const index = indexByKey.get(createdKey);
    if (index === undefined) return;

    totals[index].orders += 1;
    const amount = order.items.reduce(
      (sum, line) => sum + line.qty * line.price * (1 - (line.discount || 0) / 100),
      0
    );
    totals[index].amount += amount;
  });

  return totals;
};

const toMoney = (value, symbol) => `${symbol} ${value.toFixed(2)}`;

const DashboardPage = () => {
  const ordersMap = useSelector(selectOrders);
  const currency = useSelector((state) => state.currency);
  const symbol = currency?.currencySymbol || "";

  const orders = useMemo(() => Object.values(ordersMap || {}), [ordersMap]);

  const today = useMemo(() => {
    const start = startOfDay(new Date());
    const todayOrders = orders.filter((order) => new Date(order.createdAt || Date.now()) >= start);

    const revenue = todayOrders.reduce(
      (sum, order) =>
        sum +
        order.items.reduce(
          (lineTotal, line) =>
            lineTotal + line.qty * line.price * (1 - (line.discount || 0) / 100),
          0
        ),
      0
    );

    const gross = todayOrders.reduce(
      (sum, order) =>
        sum +
        order.items.reduce((lineTotal, line) => lineTotal + line.qty * line.price, 0),
      0
    );

    const discount = Math.max(0, gross - revenue);

    const items = todayOrders.reduce(
      (sum, order) => sum + order.items.reduce((lineSum, line) => lineSum + line.qty, 0),
      0
    );

    const avgBill = todayOrders.length ? revenue / todayOrders.length : 0;

    return {
      orders: todayOrders.length,
      revenue,
      discount,
      items,
      avgBill,
      customers: todayOrders.length,
    };
  }, [orders]);

  const weekly = useMemo(() => bucketByDay(orders, 7), [orders]);
  const monthly = useMemo(() => bucketByDay(orders, 30), [orders]);

  const weeklyOrders = weekly.reduce((sum, day) => sum + day.orders, 0);
  const weeklyRevenue = weekly.reduce((sum, day) => sum + day.amount, 0);
  const monthlyOrders = monthly.reduce((sum, day) => sum + day.orders, 0);
  const monthlyRevenue = monthly.reduce((sum, day) => sum + day.amount, 0);

  const sparkSeries = monthly.slice(-12);
  const maxSpark = Math.max(1, ...sparkSeries.map((day) => day.orders));
  const sparkPoints = sparkSeries
    .map((day, index) => {
      const x = (index / Math.max(1, sparkSeries.length - 1)) * 100;
      const y = 100 - (day.orders / maxSpark) * 100;
      return `${x},${y}`;
    })
    .join(" ");

  const topProducts = useMemo(() => {
    const map = new Map();
    orders.forEach((order) => {
      order.items.forEach((line) => {
        const key = line.prodDefId || line.title;
        const existing = map.get(key) || { title: line.title, qty: 0, revenue: 0 };
        existing.qty += line.qty;
        existing.revenue += line.qty * line.price * (1 - (line.discount || 0) / 100);
        map.set(key, existing);
      });
    });
    return Array.from(map.values())
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);
  }, [orders]);

  const liveOrders = useMemo(
    () =>
      orders
        .filter((order) => order.items.some((line) => line.status !== "served"))
        .slice(0, 6),
    [orders]
  );

  const statCards = [
    { label: "Today Orders", value: String(today.orders), icon: <ReceiptLong fontSize="small" />, note: `${weeklyOrders} this week` },
    { label: "Revenue", value: toMoney(today.revenue, symbol), icon: <AccountBalanceWallet fontSize="small" />, note: `${toMoney(weeklyRevenue, symbol)} weekly` },
    { label: "Avg Bill", value: toMoney(today.avgBill, symbol), icon: <TrendingUp fontSize="small" />, note: "Ticket average" },
    { label: "Customers", value: String(today.customers), icon: <StoreMallDirectory fontSize="small" />, note: "Walk-in + online" },
    { label: "Items Sold", value: String(today.items), icon: <ShoppingBag fontSize="small" />, note: "Units today" },
    { label: "Refunds", value: toMoney(0, symbol), icon: <WarningAmber fontSize="small" />, note: "No refund events" },
    { label: "Discounts", value: toMoney(today.discount, symbol), icon: <Payment fontSize="small" />, note: "Discount impact" },
    { label: "Open Tables", value: String(liveOrders.length), icon: <LocalDining fontSize="small" />, note: `${orders.length} active bills` },
  ];

  const totalPayments = Math.max(1, weeklyRevenue);
  const paymentLegends = [
    { label: "Cash", pct: 38, amount: (totalPayments * 0.38).toFixed(2), color: "#2f6bff" },
    { label: "Card", pct: 32, amount: (totalPayments * 0.32).toFixed(2), color: "#7c4dff" },
    { label: "UPI", pct: 20, amount: (totalPayments * 0.2).toFixed(2), color: "#f59e0b" },
    { label: "Others", pct: 10, amount: (totalPayments * 0.1).toFixed(2), color: "#ec4899" },
  ];

  const channelLegends = [
    { label: "Walk In", pct: 46, color: "#2f6bff" },
    { label: "Delivery", pct: 29, color: "#22c55e" },
    { label: "Pickup", pct: 15, color: "#a855f7" },
    { label: "Online", pct: 10, color: "#ef4444" },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <div className={styles.titleWrap}>
          <h1 className={styles.pageTitle}>Dashboard</h1>
          <span className={styles.outletBadge}>Outlet: Main Branch</span>
        </div>
        <div className={styles.timeRangePills}>
          <button className={`${styles.pill} ${styles.pillActive}`}>Today</button>
          <button className={styles.pill}>This Week</button>
          <button className={styles.pill}>Month</button>
          <button className={styles.pill}>Custom Date</button>
        </div>
      </header>

      <section className={styles.statsGrid}>
        {statCards.map((card) => (
          <article key={card.label} className={styles.statCard}>
            <div className={styles.statHead}>
              <p className={styles.statLabel}>{card.label}</p>
              <span className={styles.statIcon}>{card.icon}</span>
            </div>
            <p className={styles.statValue}>{card.value}</p>
            <p className={styles.statNote}>{card.note}</p>
          </article>
        ))}
      </section>

      <section className={styles.boardGrid}>
        <article className={`${styles.panel} ${styles.salesOverview}`}>
          <div className={styles.panelHead}>
            <h2>Sales Overview</h2>
            <span className={styles.panelSubTag}>Weekly + Monthly</span>
          </div>
          <div className={styles.overviewMeta}>
            <span>Week Orders: {weeklyOrders}</span>
            <span>Month Orders: {monthlyOrders}</span>
            <span>Month Revenue: {toMoney(monthlyRevenue, symbol)}</span>
          </div>
          <div className={styles.sparkWrap}>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={styles.sparkSvg}>
              <polyline points={sparkPoints} className={styles.sparkLine} />
              {sparkSeries.map((day, index) => {
                const x = (index / Math.max(1, sparkSeries.length - 1)) * 100;
                const y = 100 - (day.orders / maxSpark) * 100;
                return <circle key={day.key} cx={x} cy={y} r="1.3" className={styles.sparkDot} />;
              })}
            </svg>
          </div>
        </article>

        <article className={`${styles.panel} ${styles.paymentsPanel}`}>
          <div className={styles.panelHead}><h2>Sales by Payment Method</h2></div>
          <div className={styles.donutWrap}>
            <div className={styles.paymentDonut} />
            <div className={styles.legendList}>
              {paymentLegends.map((item) => (
                <div key={item.label} className={styles.legendRow}>
                  <span className={styles.legendName}><i style={{ background: item.color }} />{item.label}</span>
                  <span>{item.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className={`${styles.panel} ${styles.channelsPanel}`}>
          <div className={styles.panelHead}><h2>Order Channels</h2></div>
          <div className={styles.donutWrap}>
            <div className={styles.channelDonut} />
            <div className={styles.legendList}>
              {channelLegends.map((item) => (
                <div key={item.label} className={styles.legendRow}>
                  <span className={styles.legendName}><i style={{ background: item.color }} />{item.label}</span>
                  <span>{item.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className={`${styles.panel} ${styles.liveOrdersPanel}`}>
          <div className={styles.panelHead}><h2>Live Orders</h2></div>
          <div className={styles.liveList}>
            {liveOrders.length === 0 ? (
              <p className={styles.emptyText}>No active orders</p>
            ) : (
              liveOrders.map((order) => {
                const pending = order.items.filter((line) => line.status !== "served").length;
                return (
                  <div key={order.tableId} className={styles.liveRow}>
                    <span>#{order.invoiceNo}</span>
                   <span>Table {order.tableId}</span>
                    <span className={styles.liveStatus}>{pending} pending</span>
                  </div>
                );
              })
            )}
          </div>
        </article>

        <article className={`${styles.panel} ${styles.topItemsPanel}`}>
          <div className={styles.panelHead}><h2>Top Selling Products</h2></div>
          <div className={styles.tableHead}>
            <span>Item</span>
            <span>Qty</span>
            <span>Revenue</span>
          </div>
          {topProducts.length === 0 ? (
            <p className={styles.emptyText}>No sales yet</p>
          ) : (
            topProducts.map((item) => (
              <div key={item.title} className={styles.tableRow}>
                <span>{item.title}</span>
                <span>{item.qty}</span>
                <span>{toMoney(item.revenue, symbol)}</span>
              </div>
            ))
          )}
        </article>

        <article className={`${styles.panel} ${styles.kitchenPanel}`}>
          <div className={styles.panelHead}><h2>Kitchen Performance</h2></div>
          <div className={styles.performanceScore}><Speed fontSize="small" /> 12 min</div>
          <div className={styles.kpiMiniRow}><span>Ready Orders</span><b>{Math.max(0, weeklyOrders - 2)}</b></div>
          <div className={styles.kpiMiniRow}><span>Delayed Orders</span><b>2</b></div>
          <div className={styles.kpiMiniRow}><span>Cooking</span><b>{liveOrders.length}</b></div>
        </article>

        <article className={`${styles.panel} ${styles.cashPanel}`}>
          <div className={styles.panelHead}><h2>Cash Drawer Summary</h2></div>
          <div className={styles.cashRow}><span>Opening Cash</span><b>{toMoney(2000, symbol)}</b></div>
          <div className={styles.cashRow}><span>Cash Sales</span><b>{toMoney(weeklyRevenue * 0.38, symbol)}</b></div>
          <div className={styles.cashRow}><span>Expected Cash</span><b>{toMoney(2000 + weeklyRevenue * 0.38, symbol)}</b></div>
        </article>

        <article className={`${styles.panel} ${styles.systemPanel}`}>
          <div className={styles.panelHead}><h2>System Health</h2></div>
          <div className={styles.healthGrid}>
            <span>POS Terminal</span><span className={styles.healthOnline}>Online</span>
            <span>Kitchen Display</span><span className={styles.healthOnline}>Online</span>
            <span>Printer</span><span className={styles.healthOnline}>Online</span>
            <span>Inventory Sync</span><span className={styles.healthOnline}>Online</span>
          </div>
        </article>
      </section>

      <footer className={styles.footerMeta}>
        <DateRange fontSize="small" />
        <span>Updated from live order state every render.</span>
        <CalendarMonth fontSize="small" />
      </footer>
    </div>
  );
};

export default DashboardPage;
